import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth, safeParseFeatures } from '../../context/AuthContext';
import { getMyGCalendar, upsertGCalendar, getCurrentTenantIntegrations, getTenantWidgetInfo, getGoogleAuthUrl, disconnectGCalendar, syncGCalendarBacklog, testWhatsApp } from '../../services/api';
import PageTour from '../../components/tenant/PageTour';

const TOUR_STEPS = [
  {
    selector: '[data-tour="integrations-widget"]',
    title: 'Widget pro seu site',
    content: 'Cole o código no seu site e aparece um botão com suas cores — seus clientes acessam o sistema direto dali.',
    placement: 'bottom',
  },
  {
    selector: '[data-tour="integrations-gcal"]',
    title: 'Google Calendar',
    content: 'Conecte sua conta Google. Todo agendamento novo aparece automaticamente no seu Calendar — criar, editar ou cancelar no AtendoApp reflete lá.',
    placement: 'top',
  },
  {
    selector: '[data-tour="integrations-wa"]',
    title: 'WhatsApp automático',
    content: 'Lembretes 1 dia antes do agendamento, confirmações, cancelamentos — tudo no número oficial do seu negócio. Disponível no plano Essencial em diante.',
    placement: 'top',
  },
];

function CopyBox({ value, label }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => toast.error('Copiar manualmente'));
  };
  return (
    <div style={{ background:'var(--gray-50)', border:'1px solid var(--gray-200)', borderRadius:8, padding:'10px 14px', display:'flex', alignItems:'flex-start', gap:10 }}>
      <code style={{ flex:1, fontSize:12, wordBreak:'break-all', lineHeight:1.6, color:'var(--gray-700)', fontFamily:'monospace' }}>{value}</code>
      <button className="btn btn-secondary btn-sm" style={{ flexShrink:0 }} onClick={copy}>
        {copied ? '✓ Copiado' : 'Copiar'}
      </button>
    </div>
  );
}

export default function TenantIntegrations() {
  const { user, hasFeature, hasPermission } = useAuth();
  const location = useLocation();
  const tenant   = user?.tenant;
  const features = safeParseFeatures(tenant?.features);

  const [gcal,      setGcal]      = useState(null);
  const [wa,        setWa]        = useState({ active: false });
  const [widget,    setWidget]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing,   setTesting]   = useState(false);

  useEffect(() => {
    if (features.google_calendar && hasPermission('CONNECT_GOOGLE_CALENDAR')) {
      getMyGCalendar().then(r => setGcal(r.data.integration)).catch(() => {});
    }
    if (features.whatsapp) {
      getCurrentTenantIntegrations().then(r => {
        const row = (r.data.integrations || []).find(i => i.type === 'whatsapp');
        if (row) setWa({ active: !!row.active });
      }).catch(() => {});
    }
    if (hasPermission('MANAGE_TENANT_SETTINGS')) {
      getTenantWidgetInfo().then(r => setWidget(r.data)).catch(() => {});
    }
  }, []);

  // Handle ?gcal=connected|error after OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gcalParam = params.get('gcal');
    if (!gcalParam) return;
    if (gcalParam === 'connected') {
      toast.success('Google Calendar conectado com sucesso!');
      getMyGCalendar().then(r => setGcal(r.data.integration)).catch(() => {});
    } else if (gcalParam === 'error') {
      const reason = params.get('reason') || '';
      toast.error(`Erro ao conectar Google Calendar${reason ? `: ${reason}` : ''}. Tente novamente.`);
    }
    // Clean URL params without navigating
    window.history.replaceState({}, '', window.location.pathname);
  }, [location.search]);

  const handleGcalConnect = async () => {
    setSaving(true);
    try {
      const r = await getGoogleAuthUrl();
      window.location.href = r.data.auth_url;
      // Don't setSaving(false) — page will redirect
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao iniciar conexão com Google');
      setSaving(false);
    }
  };

  const handleGcalDisconnect = async () => {
    setSaving(true);
    try {
      // Endpoint novo: remove a linha de user_integrations (antes só setava active=0,
      // mas o token ficava armazenado — confuso porque "desconectado" não limpava)
      await disconnectGCalendar();
      setGcal(null);
      toast.success('Google Calendar desconectado.');
    } catch (err) {
      // Fallback: se o endpoint novo não existir (deploy velho), usa o antigo
      try {
        const r = await upsertGCalendar({ calendar_id: null, active: false });
        setGcal(r.data.integration);
        toast.success('Google Calendar desconectado.');
      } catch {
        toast.error(err.response?.data?.error || 'Erro');
      }
    }
    finally { setSaving(false); }
  };

  const [syncing, setSyncing] = useState(false);
  const handleGcalSyncBacklog = async () => {
    setSyncing(true);
    try {
      const r = await syncGCalendarBacklog();
      toast.success(r.data.message || 'Sincronização concluída.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao sincronizar');
    } finally { setSyncing(false); }
  };

  const handleWaTest = async () => {
    setTesting(true);
    try {
      const r = await testWhatsApp({ phone: testPhone });
      toast.success(r.data.message || 'Mensagem enviada!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao enviar mensagem de teste');
    } finally { setTesting(false); }
  };

  const gcalConnected = gcal?.active && gcal?.access_token;

  return (
    <div className="page-body">
      <div className="page-header">
        <h2>Integrações</h2>
        <p>Configure as integrações e o widget para o seu site</p>
        <div style={{ marginTop: 8 }}>
          <PageTour tourId="integrations" steps={TOUR_STEPS} />
        </div>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

        {/* Widget — Área do Cliente */}
        {hasPermission('MANAGE_TENANT_SETTINGS') && (
          <div className="card" data-tour="integrations-widget">
            <div className="card-header">
              <span className="card-title">🔗 Widget — Área do Cliente</span>
              <span className="badge badge-success">Disponível</span>
            </div>
            <div className="card-body">
              <p style={{ color:'var(--gray-500)', fontSize:13, marginBottom:16 }}>
                Permita que os usuários acessem o sistema diretamente pelo seu site. O botão aparece automaticamente com as suas cores cadastradas.
              </p>

              {widget ? (
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13, marginBottom:6 }}>🔑 Link direto de acesso</div>
                    <p style={{ fontSize:12, color:'var(--gray-500)', marginBottom:8 }}>
                      Compartilhe este link com seus usuários. A tela de login vai mostrar a identidade visual da sua empresa.
                    </p>
                    <CopyBox value={widget.login_url} />
                  </div>

                  <div style={{ borderTop:'1px solid var(--gray-100)', paddingTop:14 }}>
                    <div style={{ fontWeight:600, fontSize:13, marginBottom:6 }}>📦 Widget para o seu site</div>
                    <p style={{ fontSize:12, color:'var(--gray-500)', marginBottom:8 }}>
                      Cole este código no <code style={{ background:'var(--gray-100)', padding:'1px 5px', borderRadius:4 }}>&lt;head&gt;</code> ou antes do <code style={{ background:'var(--gray-100)', padding:'1px 5px', borderRadius:4 }}>&lt;/body&gt;</code> do seu site.
                    </p>
                    <CopyBox value={widget.embed_html} />
                  </div>

                  <div style={{ borderTop:'1px solid var(--gray-100)', paddingTop:14 }}>
                    <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>📍 Posicionamento automático</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                      {[
                        ['Detecção automática', 'O widget tenta inserir o botão no <nav>, <header> ou no primeiro menu encontrado.'],
                        ['Posicionamento manual', 'Adicione data-atendo em qualquer elemento para definir exatamente onde o botão aparece.'],
                        ['Fallback', 'Se nenhum menu for encontrado, o botão fica fixo no canto inferior direito da tela.'],
                      ].map(([t, d]) => (
                        <div key={t} style={{ display:'flex', gap:8, fontSize:12 }}>
                          <span style={{ color:'var(--primary)', fontWeight:700, flexShrink:0 }}>▸</span>
                          <span><strong>{t}:</strong> {d}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="spinner" style={{ margin:'20px auto', display:'block' }} />
              )}
            </div>
          </div>
        )}

        {/* Google Calendar */}
        {features.google_calendar && (
          <div className="card" data-tour="integrations-gcal">
            <div className="card-header">
              <span className="card-title">📆 Google Calendar</span>
              <span className={`badge ${gcalConnected ? 'badge-success' : 'badge-gray'}`}>
                {gcalConnected ? 'Conectado' : 'Desconectado'}
              </span>
            </div>
            <div className="card-body">
              {hasPermission('CONNECT_GOOGLE_CALENDAR') ? (
                gcalConnected ? (
                  <>
                    <div className="success-box" style={{ marginBottom:16 }}>
                      ✅ Google Calendar conectado. Novos agendamentos sincronizam automaticamente com o calendário <strong>primário</strong> da sua conta Google — criar/atualizar/cancelar no AtendoApp reflete no Calendar na hora.
                    </div>
                    <div style={{ background:'var(--gray-50)', border:'1px solid var(--gray-200)', borderRadius:8, padding:'12px 14px', marginBottom:16, fontSize:13 }}>
                      <div style={{ fontWeight:600, marginBottom:4 }}>🔄 Agendamentos existentes</div>
                      <p style={{ color:'var(--gray-600)', margin:'0 0 10px' }}>
                        Agendamentos criados <strong>antes</strong> de você conectar o Google não foram sincronizados ainda. Clique abaixo pra popular o Calendar retroativamente (só agendamentos futuros).
                      </p>
                      <button className={`btn btn-secondary btn-sm${syncing ? ' btn-loading' : ''}`} disabled={syncing} onClick={handleGcalSyncBacklog}>
                        {syncing ? '' : 'Sincronizar agendamentos futuros'}
                      </button>
                    </div>
                    <button className={`btn btn-danger btn-sm${saving ? ' btn-loading' : ''}`} disabled={saving} onClick={handleGcalDisconnect}>
                      {saving ? '' : 'Desconectar'}
                    </button>
                  </>
                ) : (
                  <>
                    <p style={{ color:'var(--gray-500)', fontSize:13, marginBottom:16 }}>
                      Conecte sua conta Google para sincronizar agendamentos automaticamente com seu Google Calendar. Você será redirecionado para o login do Google.
                    </p>
                    <button className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving} onClick={handleGcalConnect}>
                      {saving ? '' : '🔗 Conectar com Google'}
                    </button>
                  </>
                )
              ) : (
                <p className="text-muted">Você não tem permissão para gerenciar esta integração.</p>
              )}
            </div>
          </div>
        )}

        {/* WhatsApp */}
        {features.whatsapp && (
          <div className="card" data-tour="integrations-wa">
            <div className="card-header">
              <span className="card-title">💬 WhatsApp — Confirmação de Agendamentos</span>
              <span className={`badge ${wa.active ? 'badge-success' : 'badge-gray'}`}>
                {wa.active ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <div className="card-body">
              {wa.active ? (
                <>
                  <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'var(--gray-50)', borderRadius:8, fontSize:13, marginBottom:16 }}>
                    <span style={{ fontSize:20 }}>✅</span>
                    <span>Lembretes automáticos via WhatsApp estão <strong>ativos</strong>. O sistema envia 1 dia antes do agendamento.</span>
                  </div>

                  <div style={{ borderTop:'1px solid var(--gray-100)', paddingTop:14 }}>
                    <div style={{ fontWeight:600, fontSize:13, marginBottom:6 }}>🧪 Enviar mensagem de teste</div>
                    <p style={{ color:'var(--gray-500)', fontSize:12, marginBottom:10 }}>
                      Verifique se a integração está funcionando enviando uma mensagem de teste.
                    </p>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      <input
                        type="tel"
                        value={testPhone}
                        onChange={e => setTestPhone(e.target.value)}
                        placeholder="5511999999999"
                        className="input"
                        style={{ flex:1, minWidth:160 }}
                      />
                      <button
                        className={`btn btn-secondary${testing ? ' btn-loading' : ''}`}
                        disabled={testing || !testPhone.trim()}
                        onClick={handleWaTest}
                      >
                        {testing ? '' : 'Enviar teste'}
                      </button>
                    </div>
                    <small style={{ color:'var(--gray-400)', fontSize:11 }}>Código do país + DDD + número, sem espaços (ex: 5511999999999)</small>
                  </div>
                </>
              ) : (
                <p style={{ color:'var(--gray-500)', fontSize:13 }}>
                  A integração com WhatsApp está disponível no seu plano mas ainda não foi configurada. Entre em contato com o suporte para ativar.
                </p>
              )}
            </div>
          </div>
        )}

        {!features.google_calendar && !features.whatsapp && !hasPermission('MANAGE_TENANT_SETTINGS') && (
          <div className="empty-state">
            <div className="empty-icon">🔗</div>
            <h3>Nenhuma integração disponível</h3>
            <p>As integrações dependem do plano contratado. Entre em contato para upgrade.</p>
          </div>
        )}
      </div>
    </div>
  );
}
